import React, { useState, useEffect, useMemo } from 'react';
import { Box, VStack, Flex } from '@chakra-ui/react';
import { Icon } from 'src/components/atoms/Icon';
import { Button } from 'src/components/atoms/Button';
import {
  kpiDataService,
  KpiMetric,
  KpiCategory,
  useStartModelingMutation,
  useKpiSourcesQuery,
  UserModel,
  KpiMetricValue,
} from 'src/api/startModelingService';
import {
  useCampaignMappingsQuery,
  useCampaignMappingsColumnsQuery,
} from 'src/api/campaignMappings';
import { useAppContext } from 'src/contexts/AppContext';
import { ModelForm, ModelFormData } from '../../../organisms/ModelForm';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { useCustomToast } from 'src/hooks/useCustomToast';
import {
  TOTAL_CATEGORY_VALUE,
  TOTAL_CATEGORY_OPTION,
} from 'src/utils/kpiConstants';

const StartModelingStep: React.FC = () => {
  const { currentCustomer } = useAppContext();
  const { toastState, hideToast, showSuccess, showError } = useCustomToast();

  // Fetch campaign mappings
  const { data: campaignMappings = [] } = useCampaignMappingsQuery(
    currentCustomer?.customerName ?? '',
  );

  // Fetch media platform names
  const { data: campaignColumns = [] } = useCampaignMappingsColumnsQuery(
    currentCustomer?.customerName ?? '',
  );

  const mediaPlatforms = useMemo(() => {
    // Format source name for display (capitalize, replace underscores with spaces)
    const formatLabel = (source: string) =>
      source
        .split('_')
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(' ');

    return [
      { value: '', label: 'Select media' },
      ...campaignColumns.map((source) => ({
        value: source,
        label: formatLabel(source),
      })),
    ];
  }, [campaignColumns]);

  // State for multiple models
  const [models, setModels] = useState<ModelFormData[]>([
    {
      id: '1',
      modelName: '',
      kpiSource: '',
      kpiMetric: '',
      kpiCategory: '',
      selectedMedia: [],
      campaignSelections: {},
      searchValue: '',
    },
  ]);

  // State for dynamic data
  const [kpiMetrics, setKpiMetrics] = useState<KpiMetric[]>([]);
  const [kpiCategories, setKpiCategories] = useState<KpiCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [isModelingStarted, setIsModelingStarted] = useState(false);

  const { data: kpiSources = [], isLoading: isLoadingKpiSources } =
    useKpiSourcesQuery(currentCustomer?.customerName ?? '');

  const startModelingMutation = useStartModelingMutation();

  // Compute searched campaigns for a specific model
  const getSearchedCampaigns = (searchValue: string) => {
    if (!searchValue.trim()) {
      return campaignMappings;
    }
    return campaignMappings.filter((campaign) =>
      campaign.campaign_name.toLowerCase().includes(searchValue.toLowerCase()),
    );
  };

  // Toggle sort order
  const handleSortToggle = () => {
    if (sortOrder === null) {
      setSortOrder('asc');
    } else if (sortOrder === 'asc') {
      setSortOrder('desc');
    } else {
      setSortOrder(null);
    }
  };

  // Initialize selected media and campaign selections when data loads
  useEffect(() => {
    const availablePlatforms = mediaPlatforms
      .filter((platform) => platform.value !== '')
      .map((platform) => platform.value);

    if (availablePlatforms.length > 0 && campaignMappings.length > 0) {
      setModels((prevModels) =>
        prevModels.map((model) => {
          const selectedMedia =
            model.selectedMedia.length === 0
              ? availablePlatforms
              : model.selectedMedia;

          if (
            Object.keys(model.campaignSelections).length > 0 &&
            model.selectedMedia.length > 0
          ) {
            return model;
          }

          // Initialize campaign selections
          const campaignSelections: {
            [campaignId: string]: { [media: string]: boolean };
          } = {};

          campaignMappings.forEach((campaign) => {
            campaignSelections[campaign.campaign_id.toString()] = {};
            selectedMedia.forEach((media) => {
              // Preselect all campaigns for all media platforms
              campaignSelections[campaign.campaign_id.toString()][media] = true;
            });
          });

          return {
            ...model,
            selectedMedia,
            campaignSelections,
          };
        }),
      );
    }
  }, [mediaPlatforms, campaignMappings]);

  // Handler functions
  const handleModelNameChange = (modelId: string, value: string) => {
    setModels((prevModels) =>
      prevModels.map((model) =>
        model.id === modelId ? { ...model, modelName: value } : model,
      ),
    );
  };

  const handleSearchValueChange = (modelId: string, value: string) => {
    setModels((prevModels) =>
      prevModels.map((model) =>
        model.id === modelId ? { ...model, searchValue: value } : model,
      ),
    );
  };

  const handleSourceChange = async (modelId: string, value: string) => {
    setModels((prevModels) =>
      prevModels.map((model) =>
        model.id === modelId ? { ...model, kpiSource: value } : model,
      ),
    );

    if (!currentCustomer?.customerName) {
      return;
    }

    setIsLoading(true);
    try {
      const [metrics, categories] = await Promise.all([
        kpiDataService.getMetrics(currentCustomer.customerName, value),
        kpiDataService.getCategories(currentCustomer.customerName, value),
      ]);

      setKpiMetrics(metrics);
      setKpiCategories([TOTAL_CATEGORY_OPTION, ...categories]);
    } catch {
      setKpiMetrics([]);
      setKpiCategories([]);
      showError(
        'Failed to load KPI data',
        'Please try again or contact support if the issue persists',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSourceReset = (modelId: string) => {
    setModels((prevModels) =>
      prevModels.map((model) =>
        model.id === modelId
          ? { ...model, kpiSource: '', kpiMetric: '', kpiCategory: '' }
          : model,
      ),
    );
  };

  const handleMetricChange = (modelId: string, value: string) => {
    setModels((prevModels) =>
      prevModels.map((model) =>
        model.id === modelId ? { ...model, kpiMetric: value } : model,
      ),
    );
  };

  const handleMetricReset = (modelId: string) => {
    setModels((prevModels) =>
      prevModels.map((model) =>
        model.id === modelId ? { ...model, kpiMetric: '' } : model,
      ),
    );
  };

  const handleCategoryChange = (modelId: string, value: string) => {
    setModels((prevModels) =>
      prevModels.map((model) =>
        model.id === modelId ? { ...model, kpiCategory: value } : model,
      ),
    );
  };

  const handleCategoryReset = (modelId: string) => {
    setModels((prevModels) =>
      prevModels.map((model) =>
        model.id === modelId ? { ...model, kpiCategory: '' } : model,
      ),
    );
  };

  const handleMediaChange = (modelId: string, index: number, value: string) => {
    setModels((prevModels) =>
      prevModels.map((model) => {
        if (model.id === modelId) {
          const newSelectedMedia = [...model.selectedMedia];
          newSelectedMedia[index] = value;

          // Update campaign selections for the new media
          const newCampaignSelections = { ...model.campaignSelections };
          Object.keys(newCampaignSelections).forEach((campaignId) => {
            newCampaignSelections[campaignId][value] = false;
          });

          return {
            ...model,
            selectedMedia: newSelectedMedia,
            campaignSelections: newCampaignSelections,
          };
        }
        return model;
      }),
    );
  };

  const handleAddColumn = (modelId: string) => {
    setModels((prevModels) =>
      prevModels.map((model) => {
        if (model.id === modelId) {
          const availableMedia = mediaPlatforms.find(
            (platform) => !model.selectedMedia.includes(platform.value),
          );

          if (availableMedia) {
            const newSelectedMedia = [
              ...model.selectedMedia,
              availableMedia.value,
            ];

            // Update campaign selections for the new media
            const newCampaignSelections = { ...model.campaignSelections };
            Object.keys(newCampaignSelections).forEach((campaignId) => {
              newCampaignSelections[campaignId][availableMedia.value] = false;
            });

            return {
              ...model,
              selectedMedia: newSelectedMedia,
              campaignSelections: newCampaignSelections,
            };
          }
        }
        return model;
      }),
    );
  };

  const handleRemoveColumn = (modelId: string) => {
    setModels((prevModels) =>
      prevModels.map((model) => {
        if (model.id === modelId && model.selectedMedia.length > 1) {
          const mediaToRemove =
            model.selectedMedia[model.selectedMedia.length - 1];
          const newSelectedMedia = model.selectedMedia.slice(0, -1);

          // Remove campaign selections for the removed media
          const newCampaignSelections = { ...model.campaignSelections };
          Object.keys(newCampaignSelections).forEach((campaignId) => {
            delete newCampaignSelections[campaignId][mediaToRemove];
          });

          return {
            ...model,
            selectedMedia: newSelectedMedia,
            campaignSelections: newCampaignSelections,
          };
        }
        return model;
      }),
    );
  };

  const handleCampaignToggle = (
    modelId: string,
    campaignId: string,
    media: string,
  ) => {
    setModels((prevModels) =>
      prevModels.map((model) => {
        if (model.id === modelId) {
          const newCampaignSelections = { ...model.campaignSelections };
          newCampaignSelections[campaignId] = {
            ...newCampaignSelections[campaignId],
            [media]: !newCampaignSelections[campaignId]?.[media],
          };

          return {
            ...model,
            campaignSelections: newCampaignSelections,
          };
        }
        return model;
      }),
    );
  };

  const handleSelectAllForMedia = (modelId: string, media: string) => {
    setModels((prevModels) =>
      prevModels.map((model) => {
        if (model.id === modelId) {
          const newCampaignSelections = { ...model.campaignSelections };
          const isAllSelected = Object.values(newCampaignSelections).every(
            (selections) => selections[media],
          );

          Object.keys(newCampaignSelections).forEach((campaignId) => {
            newCampaignSelections[campaignId] = {
              ...newCampaignSelections[campaignId],
              [media]: !isAllSelected,
            };
          });

          return {
            ...model,
            campaignSelections: newCampaignSelections,
          };
        }
        return model;
      }),
    );
  };

  const handleAddNewModel = () => {
    const newModelId = (models.length + 1).toString();
    const newModel: ModelFormData = {
      id: newModelId,
      modelName: '',
      kpiSource: '',
      kpiMetric: '',
      kpiCategory: '',
      selectedMedia: mediaPlatforms
        .filter((platform) => platform.value !== '')
        .map((platform) => platform.value),
      campaignSelections: {},
      searchValue: '',
    };

    // Initialize campaign selections for the new model with all campaigns preselected
    const campaignSelections: {
      [campaignId: string]: { [media: string]: boolean };
    } = {};

    campaignMappings.forEach((campaign) => {
      campaignSelections[campaign.campaign_id.toString()] = {};
      newModel.selectedMedia.forEach((media) => {
        // Preselect all campaigns for all media platforms
        campaignSelections[campaign.campaign_id.toString()][media] = true;
      });
    });

    newModel.campaignSelections = campaignSelections;

    setModels((prevModels) => [...prevModels, newModel]);
  };

  const handleRemoveModel = (modelId: string) => {
    setModels((prevModels) =>
      prevModels.filter((model) => model.id !== modelId),
    );
  };

  // Helper functions
  const isModelFormValid = (model: ModelFormData) => {
    return (
      model.modelName.trim() !== '' &&
      model.kpiSource !== '' &&
      model.kpiMetric !== '' &&
      model.selectedMedia.some((media) => media !== '') &&
      Object.values(model.campaignSelections).some((selections) =>
        Object.values(selections).some((isSelected) => isSelected),
      )
    );
  };

  const hasAvailableMedia = (model: ModelFormData) => {
    return model.selectedMedia.some(
      (media) => !mediaPlatforms.find((platform) => platform.value === media),
    );
  };

  const isAllCampaignsSelectedForMedia = (
    model: ModelFormData,
    media: string,
  ) => {
    return Object.values(model.campaignSelections).every(
      (selections) => selections[media],
    );
  };

  const areAllModelsValid = models.every(isModelFormValid);

  const handleStartModeling = () => {
    if (!currentCustomer?.customerName) {
      return;
    }

    const transformedModels: UserModel[] = models.map((model) => ({
      modelName: model.modelName.trim(),
      kpiSource: 'uploaded_file' as const,
      kpiMetric: model.kpiMetric as KpiMetricValue,
      kpiCategory:
        model.kpiCategory === TOTAL_CATEGORY_VALUE
          ? null
          : model.kpiCategory.trim() || null,
      selectedMedia: model.selectedMedia.filter((media) => media !== ''),
      campaignSelections: model.campaignSelections,
    }));

    startModelingMutation.mutate(
      {
        customer: currentCustomer.customerName,
        models: transformedModels,
      },
      {
        onSuccess: (data) => {
          setIsModelingStarted(true);
          showSuccess(
            'Models started successfully',
            'Your models are now running. You can proceed to the next step.',
          );
        },
        onError: (error) => {
          showError(
            'Start modeling failed',
            'Please try again or contact support if the issue persists',
          );
        },
      },
    );
  };

  return (
    <VStack spacing={8} align="stretch">
      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        duration={toastState.duration}
        onClose={hideToast}
        position={{ top: '90px', right: '20px' }}
      />

      {/* Render all model forms */}
      {models.map((model, index) => (
        <Box key={model.id}>
          <ModelForm
            model={model}
            modelIndex={index}
            onRemove={() => handleRemoveModel(model.id)}
            canRemove={models.length > 1}
            modelsLength={models.length}
            onModelNameChange={handleModelNameChange}
            onSourceChange={handleSourceChange}
            onSourceReset={handleSourceReset}
            onMetricChange={handleMetricChange}
            onMetricReset={handleMetricReset}
            onCategoryChange={handleCategoryChange}
            onCategoryReset={handleCategoryReset}
            onMediaChange={handleMediaChange}
            onAddColumn={handleAddColumn}
            onRemoveColumn={handleRemoveColumn}
            onCampaignToggle={handleCampaignToggle}
            onSelectAllForMedia={handleSelectAllForMedia}
            onSearchValueChange={handleSearchValueChange}
            kpiSources={kpiSources}
            kpiMetrics={kpiMetrics}
            kpiCategories={kpiCategories}
            isLoading={isLoading || isLoadingKpiSources}
            mediaPlatforms={mediaPlatforms}
            campaignMappings={campaignMappings}
            searchedCampaigns={getSearchedCampaigns(model.searchValue)}
            sortOrder={sortOrder}
            handleSortToggle={handleSortToggle}
            isAllCampaignsSelectedForMedia={isAllCampaignsSelectedForMedia}
            hasAvailableMedia={hasAvailableMedia}
          />
        </Box>
      ))}

      {/* Action Buttons */}
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justify={{ base: 'stretch', md: 'flex-end' }}
        alignItems="center"
        gap={4}
        mb={10}>
        <Button
          buttonName="Add new model"
          leftIcon={<Icon iconName="plus" />}
          variant="outline"
          borderColor="icon.green"
          color="text.title"
          bg="surface.card"
          borderRadius="lg"
          w={{ base: '100%', md: '240px' }}
          py={3}
          alignSelf={'center'}
          fontSize="16px"
          fontWeight={600}
          onClick={handleAddNewModel}
          isDisabled={isModelingStarted}
          _hover={{
            bg: 'icon.green',
            color: 'white',
          }}>
          Add new model (290 €/mo)
        </Button>

        <Button
          buttonName="Start Modeling"
          onClick={handleStartModeling}
          isLoading={startModelingMutation.isLoading}
          loadingText="Starting..."
          bg="icon.green"
          color="white"
          borderRadius="lg"
          w={{ base: '100%', md: '240px' }}
          py={3}
          alignSelf={'center'}
          fontSize="16px"
          fontWeight={600}
          isDisabled={
            !areAllModelsValid ||
            startModelingMutation.isLoading ||
            isModelingStarted
          }
          _hover={{
            opacity: 0.9,
          }}
          _disabled={{
            bg: 'icon.green',
            cursor: 'not-allowed',
            opacity: 0.5,
          }}>
          Start Modeling
        </Button>
      </Flex>
    </VStack>
  );
};

export default StartModelingStep;
